import { useState, useEffect, useCallback } from "react";
import { API, authFetch, useEditMode } from "@/App";
import { toast } from "sonner";
import {
  Package, Plus, Search, Edit, Trash2, ArrowUp, ArrowDown,
  AlertTriangle, TrendingDown, Save, Warehouse, Filter,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "RAW_MATERIAL", label: "Raw Materials" },
  { value: "FINISHED_GOODS", label: "Finished Goods" },
  { value: "PACKAGING", label: "Packaging" },
];

const InventoryPage = () => {
  const canEdit = useEditMode();
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [unit, setUnit] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [adjustDialog, setAdjustDialog] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ type: "RECEIPT", quantity: 0, reason: "" });
  const [form, setForm] = useState({
    item_name: "", category: "FINISHED_GOODS", unit: "Unit 1", warehouse: "Main",
    uom: "MT", current_stock: 0, reorder_level: 0, max_stock: 0, unit_cost: 0, supplier: "", auto_reorder: true,
  });

  const fetchData = useCallback(async () => {
    let url = `${API}/inventory?`;
    if (category !== "all") url += `category=${category}&`;
    if (unit !== "all") url += `unit=${unit}&`;
    const res = await authFetch(url);
    const data = await res.json();
    if (data.success) { setItems(data.data); setAlerts(data.alerts || []); }
    setLoading(false);
  }, [category, unit]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = items.filter(i => !search || i.item_name?.toLowerCase().includes(search.toLowerCase()));

  const handleCreate = async () => {
    const res = await authFetch(`${API}/inventory`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
    });
    const data = await res.json();
    if (data.success) { toast.success("Item added"); setShowCreate(false); fetchData(); }
    else toast.error(data.detail || "Failed");
  };

  const handleUpdate = async () => {
    if (!editItem) return;
    const res = await authFetch(`${API}/inventory/${editItem.item_id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(editItem),
    });
    const data = await res.json();
    if (data.success) { toast.success("Updated"); setEditItem(null); fetchData(); }
    else toast.error("Failed");
  };

  const handleAdjust = async () => {
    if (!adjustDialog) return;
    const res = await authFetch(`${API}/inventory/${adjustDialog.item_id}/adjust`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(adjustForm),
    });
    const data = await res.json();
    if (data.success) { toast.success(`Stock ${adjustForm.type.toLowerCase()} recorded`); setAdjustDialog(null); setAdjustForm({ type: "RECEIPT", quantity: 0, reason: "" }); fetchData(); }
    else toast.error("Failed");
  };

  const stockPct = (item) => {
    const max = item.max_stock || 1;
    return Math.min(100, Math.round((item.current_stock / max) * 100));
  };

  const stockColor = (item) => {
    if (item.current_stock <= (item.reorder_level || 0) * 0.5) return "bg-red-500";
    if (item.current_stock <= (item.reorder_level || 0)) return "bg-amber-500";
    return "bg-emerald-500";
  };

  // Stats
  const totalItems = items.length;
  const lowStock = items.filter(i => i.current_stock <= (i.reorder_level || 0)).length;
  const totalValue = items.reduce((s, i) => s + (i.current_stock || 0) * (i.unit_cost || 0), 0);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Sora']">Inventory Management</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Raw materials, finished goods & packaging stock</p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreate(true)} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="add-inventory-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Item
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 dark:bg-slate-800"><p className="text-2xl font-bold text-slate-900 dark:text-white font-mono">{totalItems}</p><p className="text-xs text-slate-500">Total Items</p></Card>
        <Card className="p-4 dark:bg-slate-800"><p className="text-2xl font-bold text-red-600 font-mono">{lowStock}</p><p className="text-xs text-slate-500">Low Stock Alerts</p></Card>
        <Card className="p-4 dark:bg-slate-800"><p className="text-2xl font-bold text-emerald-600 font-mono">{items.filter(i => i.category === "FINISHED_GOODS").length}</p><p className="text-xs text-slate-500">Finished Goods</p></Card>
        <Card className="p-4 dark:bg-slate-800"><p className="text-2xl font-bold text-blue-600 font-mono">Rs {(totalValue / 100000).toFixed(1)}L</p><p className="text-xs text-slate-500">Stock Value</p></Card>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <Card className="p-3 border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/20 dark:border-slate-700" data-testid="inventory-alerts">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-600" /><span className="font-semibold text-sm text-red-700 dark:text-red-400">Reorder Alerts ({alerts.length})</span></div>
          <div className="flex flex-wrap gap-2">
            {alerts.map(a => (
              <Badge key={a.item_id} className={`text-xs ${a.severity === "CRITICAL" ? "bg-red-600 text-white" : "bg-amber-100 text-amber-700"}`}>
                {a.item_name}: {a.current_stock} {a.severity === "CRITICAL" ? "(CRITICAL)" : "(LOW)"}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items..." className="pl-9" data-testid="inventory-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44" data-testid="category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={unit} onValueChange={setUnit}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Units</SelectItem>
            <SelectItem value="Unit 1">Unit 1</SelectItem>
            <SelectItem value="Unit 2">Unit 2</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Inventory Table */}
      <Card className="overflow-hidden dark:bg-slate-800">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="inventory-table">
            <thead><tr><th>Item</th><th>Category</th><th>Unit</th><th>Stock</th><th>Level</th><th>Reorder</th><th>Cost</th><th>Modified By</th>{canEdit && <th>Actions</th>}</tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-slate-500">No inventory items</td></tr>
              ) : filtered.map(item => (
                <tr key={item.item_id} data-testid={`inv-row-${item.item_id}`}>
                  <td className="font-medium text-sm dark:text-white">{item.item_name}</td>
                  <td><Badge variant="outline" className="text-[10px]">{item.category}</Badge></td>
                  <td className="text-xs text-slate-500">{item.unit}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold dark:text-white">{item.current_stock}</span>
                      <span className="text-[10px] text-slate-400">{item.uom}</span>
                    </div>
                    <div className="w-20 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full mt-0.5">
                      <div className={`h-full rounded-full ${stockColor(item)}`} style={{ width: `${stockPct(item)}%` }} />
                    </div>
                  </td>
                  <td className="text-xs text-slate-500">{stockPct(item)}%</td>
                  <td className="text-xs">{item.current_stock <= (item.reorder_level || 0) ? <Badge className="bg-red-100 text-red-600 text-[10px]"><TrendingDown className="w-2.5 h-2.5 mr-0.5" />Low</Badge> : <span className="text-slate-400">OK</span>}</td>
                  <td className="text-xs text-slate-500 font-mono">Rs {(item.unit_cost || 0).toLocaleString()}</td>
                  <td className="text-[10px] text-slate-400">{item.last_modified_by || "—"}</td>
                  {canEdit && (
                    <td>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-600" onClick={() => { setAdjustDialog(item); setAdjustForm({ type: "RECEIPT", quantity: 0, reason: "" }); }} data-testid={`adjust-${item.item_id}`}><ArrowUp className="w-3 h-3 mr-0.5" />In</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-600" onClick={() => { setAdjustDialog(item); setAdjustForm({ type: "ISSUE", quantity: 0, reason: "" }); }} data-testid={`issue-${item.item_id}`}><ArrowDown className="w-3 h-3 mr-0.5" />Out</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditItem({ ...item })} data-testid={`edit-inv-${item.item_id}`}><Edit className="w-3 h-3" /></Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="create-inventory-dialog">
          <DialogHeader><DialogTitle>Add Inventory Item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={form.item_name} onChange={e => setForm({ ...form, item_name: e.target.value })} placeholder="Item Name" data-testid="inv-item-name" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="RAW_MATERIAL">Raw Material</SelectItem><SelectItem value="FINISHED_GOODS">Finished Goods</SelectItem><SelectItem value="PACKAGING">Packaging</SelectItem></SelectContent>
              </Select>
              <Select value={form.unit} onValueChange={v => setForm({ ...form, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Unit 1">Unit 1</SelectItem><SelectItem value="Unit 2">Unit 2</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-slate-500">Current Stock</label><Input type="number" value={form.current_stock} onChange={e => setForm({ ...form, current_stock: parseFloat(e.target.value) || 0 })} /></div>
              <div><label className="text-xs text-slate-500">Reorder Level</label><Input type="number" value={form.reorder_level} onChange={e => setForm({ ...form, reorder_level: parseFloat(e.target.value) || 0 })} /></div>
              <div><label className="text-xs text-slate-500">Max Stock</label><Input type="number" value={form.max_stock} onChange={e => setForm({ ...form, max_stock: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Unit Cost (Rs)</label><Input type="number" value={form.unit_cost} onChange={e => setForm({ ...form, unit_cost: parseFloat(e.target.value) || 0 })} /></div>
              <div><label className="text-xs text-slate-500">Supplier</label><Input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} className="bg-[#8B1A1A] text-white" data-testid="confirm-add-inv">Add</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit: {editItem?.item_name}</DialogTitle></DialogHeader>
          {editItem && (
            <div className="space-y-3">
              <Input value={editItem.item_name} onChange={e => setEditItem({ ...editItem, item_name: e.target.value })} />
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs text-slate-500">Reorder Level</label><Input type="number" value={editItem.reorder_level} onChange={e => setEditItem({ ...editItem, reorder_level: parseFloat(e.target.value) || 0 })} /></div>
                <div><label className="text-xs text-slate-500">Max Stock</label><Input type="number" value={editItem.max_stock} onChange={e => setEditItem({ ...editItem, max_stock: parseFloat(e.target.value) || 0 })} /></div>
                <div><label className="text-xs text-slate-500">Unit Cost</label><Input type="number" value={editItem.unit_cost} onChange={e => setEditItem({ ...editItem, unit_cost: parseFloat(e.target.value) || 0 })} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button><Button onClick={handleUpdate} className="bg-[#8B1A1A] text-white"><Save className="w-3.5 h-3.5 mr-1" />Save</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={!!adjustDialog} onOpenChange={() => setAdjustDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="adjust-stock-dialog">
          <DialogHeader><DialogTitle>{adjustForm.type === "RECEIPT" ? "Stock In" : "Stock Out"}: {adjustDialog?.item_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Current: <strong>{adjustDialog?.current_stock} {adjustDialog?.uom}</strong></p>
            <Select value={adjustForm.type} onValueChange={v => setAdjustForm({ ...adjustForm, type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="RECEIPT">Receipt (In)</SelectItem><SelectItem value="ISSUE">Issue (Out)</SelectItem></SelectContent>
            </Select>
            <div><label className="text-xs text-slate-500">Quantity</label><Input type="number" value={adjustForm.quantity} onChange={e => setAdjustForm({ ...adjustForm, quantity: parseFloat(e.target.value) || 0 })} data-testid="adjust-quantity" /></div>
            <div><label className="text-xs text-slate-500">Reason</label><Input value={adjustForm.reason} onChange={e => setAdjustForm({ ...adjustForm, reason: e.target.value })} placeholder="e.g. Production batch #123" /></div>
            <DialogFooter><Button variant="outline" onClick={() => setAdjustDialog(null)}>Cancel</Button><Button onClick={handleAdjust} className={`text-white ${adjustForm.type === "RECEIPT" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}`} data-testid="confirm-adjust">{adjustForm.type === "RECEIPT" ? "Record Receipt" : "Record Issue"}</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
